// Wire shapes → view models, and the one place RAG tiers are attached.
//
// The API deliberately returns raw indicator values with no tiering, so the
// provisional bands in ./methodology stay the single source of truth (PEAK-185).
// Everything a view renders as green/amber/red is decided here.

import { METRIC_DEFINITIONS, RAG_RANGES, tierForMetric, tierForScore, worstTier } from './methodology';
import type { ApiCohortDaily, ApiCohortRow, ApiFatigueSummary, ApiIndividuals } from './fatigueApi';
import type {
  CohortDailyPoint,
  CohortFatigueSummary,
  DerivedMetricKey,
  FatigueDerivedMetric,
  FatigueFilters,
  FatigueSummary,
  FatigueTrendPoint,
  IndividualFatigueRisk,
  TrendDirection,
} from './types';

const METRIC_KEYS: DerivedMetricKey[] = ['fed', 'lrm', 'cdi', 'pdc'];

/**
 * 0–100 normalisation for rings and gauges. FED is a rate per 1,000 workdays so
 * it needs scaling; the other three are already on a 0–100-ish scale.
 */
const fillFor = (key: DerivedMetricKey, value: number): number =>
  key === 'fed' ? Math.min(100, Math.round(value / 2)) : Math.min(100, Math.round(value));

/** Change is only a direction once it clears the noise floor for that metric. */
const NOISE_FLOOR: Record<DerivedMetricKey, number> = { fed: 5, lrm: 2, cdi: 2, pdc: 2 };

const directionFor = (key: DerivedMetricKey, delta: number): TrendDirection => {
  if (Math.abs(delta) < NOISE_FLOOR[key]) return 'stable';
  return delta > 0 ? 'up' : 'down';
};

const signed = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value)}`;

/**
 * Headline cards from the org values plus the measured trend.
 *
 * Both comparison labels come from the trend series rather than a separate
 * baseline call: the last fortnight against the one before it for the pill, and
 * against the oldest measured fortnight for the baseline line. Buckets the API
 * flagged as empty are skipped so a gap in coverage does not read as a drop to
 * zero.
 */
export const buildMetrics = (
  values: Record<DerivedMetricKey, number>,
  trend: FatigueTrendPoint[],
): FatigueDerivedMetric[] => {
  const measured = trend.filter((point) => !point.empty);
  const latest = measured[measured.length - 1];
  const previous = measured[measured.length - 2];
  const earliest = measured[0];

  return METRIC_KEYS.map((key) => {
    const def = METRIC_DEFINITIONS[key];
    const value = values[key];
    const fortnightDelta = latest && previous ? latest[key] - previous[key] : 0;
    const windowDelta = latest && earliest && earliest !== latest ? latest[key] - earliest[key] : 0;

    return {
      key,
      code: def.code,
      name: def.name,
      question: def.question,
      value,
      unit: RAG_RANGES[key].unit,
      fill: fillFor(key, value),
      trend: directionFor(key, fortnightDelta),
      trendLabel: previous ? `${signed(fortnightDelta)} vs last fortnight` : 'No prior period',
      baselineLabel:
        earliest && earliest !== latest
          ? `${signed(windowDelta)} vs ${earliest.period.replace('Wk-', '')} weeks ago`
          : 'No earlier history',
      goodDirection: 'down',
      riskTier: tierForMetric(key, value),
    };
  });
};

const mapCohort = (row: ApiCohortRow): CohortFatigueSummary => {
  // A cohort takes the worst tier of its four indicators. A suppressed or
  // empty cohort has no values to tier, so it reads green rather than being
  // coloured off zeros it never reported.
  const riskTier =
    row.suppressed || row.noData
      ? 'green'
      : worstTier([
          tierForMetric('fed', row.fedPer1000),
          tierForMetric('lrm', row.lrmPct),
          tierForMetric('cdi', row.cdiScore),
          tierForMetric('pdc', row.pdcPct),
        ]);

  return {
    cohort: { ...row.cohort, riskTier },
    fedPer1000: row.fedPer1000,
    fedPctWorkdays: row.fedPctWorkdays,
    lrmPct: row.lrmPct,
    cdiScore: row.cdiScore,
    pdcPct: row.pdcPct,
    trend: row.trend,
    riskTier,
    sparkline: row.sparkline,
    compliance: row.compliance,
    suppressed: row.suppressed,
    noData: row.noData,
  };
};

export const mapSummary = (api: ApiFatigueSummary): FatigueSummary => ({
  window: api.window,
  metrics: buildMetrics(api.metrics, api.trend),
  cohorts: api.cohorts.map(mapCohort),
  coverage: api.coverage,
  trend: api.trend,
  caveats: api.caveats,
  empty: api.empty,
});

export const mapCohortDaily = (api: ApiCohortDaily): CohortDailyPoint[] => api.series;

export const mapIndividuals = (api: ApiIndividuals): IndividualFatigueRisk[] =>
  api.individuals.map((person) => ({
    id: person.userId,
    name: person.name,
    jobRole: person.jobRole,
    fatigueScore: person.score,
    riskTier: tierForScore(person.score),
    dayCount: person.dayCount,
    fedPct: person.fedPct,
    lrmPct: person.lrmPct,
    cdiScore: person.cdiScore,
    inPdc: person.inPdc,
    avgSleepHours: person.avgSleepHours,
    avgReadiness: person.avgReadiness,
    avgStrain: person.avgStrain,
    // Per-person confidence is simply how much of the window they wore a device
    // for. The cohort figure blends coverage and completeness; for one person
    // coverage is either 0 or 100, so only completeness says anything.
    dataConfidence: api.window.days ? Math.round((person.dayCount / api.window.days) * 100) : 0,
  }));

// ── Client-side filters ─────────────────────────────────────────────────────
//
// Site and shift type are attributes the API derives per cohort (modal depot,
// and shift inferred from the work-hours answer). The server's own filter params
// take depot ids and raw onboarding answers, so filtering on the derived labels
// happens here, where those labels exist. The data-quality filter is different —
// it changes which people are in scope, so it goes to the API.

export const filterCohorts = (cohorts: CohortFatigueSummary[], filters: FatigueFilters): CohortFatigueSummary[] =>
  cohorts.filter((row) => {
    if (filters.site !== 'All sites' && row.cohort.site !== filters.site) return false;
    if (filters.shiftType !== 'All shifts' && row.cohort.shiftType !== filters.shiftType) return false;
    return true;
  });

export const isFiltered = (filters: FatigueFilters): boolean =>
  filters.site !== 'All sites' || filters.shiftType !== 'All shifts';

/**
 * Headline values recomputed over a subset of cohorts, weighted by the
 * person-days behind each one.
 *
 * Only used when a site or shift filter is active — with no filter the API's own
 * org roll-up is used as-is. Person-days are the right weight because that is
 * how the API pools FED and LRM in the first place; it is still an approximation
 * of re-running the query over the subset, so it is only ever shown alongside
 * the cohort count it was built from.
 */
export const rollUpCohortMetrics = (rows: CohortFatigueSummary[]): Record<DerivedMetricKey, number> => {
  const usable = rows.filter((row) => !row.noData && !row.suppressed);
  if (!usable.length) return { fed: 0, lrm: 0, cdi: 0, pdc: 0 };

  const weights = usable.map((row) => Math.max(1, row.compliance.validDataDays));
  const total = weights.reduce((sum, w) => sum + w, 0);
  const weighted = (pick: (row: CohortFatigueSummary) => number) =>
    Math.round(usable.reduce((sum, row, i) => sum + pick(row) * weights[i], 0) / total);

  return {
    fed: weighted((r) => r.fedPer1000),
    lrm: weighted((r) => r.lrmPct),
    cdi: weighted((r) => r.cdiScore),
    pdc: weighted((r) => r.pdcPct),
  };
};

/** Coverage recomputed over a subset of cohorts, for the same reason. */
export const rollUpCoverage = (rows: CohortFatigueSummary[]): FatigueSummary['coverage'] => {
  const parts = rows.map((row) => row.compliance);
  const enrolled = parts.reduce((sum, c) => sum + c.enrolledUsers, 0);
  const active = parts.reduce((sum, c) => sum + c.activeUsers, 0);
  const validDataDays = parts.reduce((sum, c) => sum + c.validDataDays, 0);
  const missingDataDays = parts.reduce((sum, c) => sum + c.missingDataDays, 0);
  const possible = validDataDays + missingDataDays;

  return {
    cohortId: '__filtered__',
    cohortName: 'Selected cohorts',
    activeUsers: active,
    enrolledUsers: enrolled,
    validDataDays,
    missingDataDays,
    coveragePct: enrolled ? Math.round((active / enrolled) * 100) : 0,
    confidenceScore: enrolled
      ? Math.round((active / enrolled) * 100 * 0.5 + (possible ? (validDataDays / possible) * 100 : 0) * 0.5)
      : 0,
  };
};

/** Distinct sites across the cohorts, for the global site filter. */
export const sitesFrom = (cohorts: CohortFatigueSummary[]): string[] =>
  Array.from(new Set(cohorts.map((row) => row.cohort.site))).sort();
